import { useParams } from 'react-router-dom';

import styled from 'styled-components';

import {
	useGetCreatorContents,
	useGetCreatorGroupPerformances,
} from 'hooks/queries/creator';
import CoreLoading from 'shared/CoreLoading';
import {
	EmphasizeText,
	ReportSectionCard,
	ReportTopHeader,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

interface IGroupsPerformances {
	avgLikeCount: number;
	avgCommentCount: number;
	avgViewCount: number;
	avgEngagement: number;
	avgSentCount: number;
	avgClickCount: number;
	advertisingCost: number;
	CPV: number;
	CPE: number;
}
type GroupPerformanceKeys = keyof IGroupsPerformances;
const performanceKeysMap: Record<GroupPerformanceKeys, string> = {
	avgLikeCount: '평균 좋아요 수(릴스/스타일클립)',
	avgCommentCount: '평균 댓글 수',
	avgViewCount: '평균 조회 수',
	avgEngagement: '평균 인게이지먼트',
	avgSentCount: '평균 발송 수(소셜비즈)',
	avgClickCount: '평균 클릭 수(소셜비즈)',
	advertisingCost: '광고 비용',
	CPV: 'CPV',
	CPE: 'CPE',
};

const performanceKeys = Object.keys(performanceKeysMap) as GroupPerformanceKeys[];

function CreatorAverageCard() {
	const { groupId, creatorPk } = useParams();

	const { data: groupPerformances, isLoading } = useGetCreatorGroupPerformances(
		groupId,
		creatorPk,
	);

	const PerformanceValue = ({
		performanceKey,
		value,
	}: {
		performanceKey: GroupPerformanceKeys;
		value: string | number | null;
	}) => {
		if (value === null) return <span>{'-'}</span>;

		if (performanceKey === 'avgLikeCount') {
			return <span>{value} 개</span>;
		}
		if (
			performanceKey === 'avgCommentCount' ||
			performanceKey === 'avgViewCount' ||
			performanceKey === 'avgEngagement'
		) {
			const formattedValue = Math.trunc(Number(value)).toLocaleString();
			return <span>{formattedValue} 개</span>;
		}

		if (performanceKey === 'avgSentCount' || performanceKey === 'avgClickCount') {
			const formattedValue = Math.trunc(Number(value)).toLocaleString();
			return <span>{formattedValue} 회</span>;
		}
		if (performanceKey === 'advertisingCost') {
			const formattedValue = Math.floor(Number(value)).toLocaleString();
			return <span>{formattedValue} 원</span>;
		}

		if (performanceKey === 'CPV' || performanceKey === 'CPE') {
			const formattedValue = Number(value).toFixed(1).toLocaleString();
			return <span>{formattedValue} 원</span>;
		}
		const formattedValue = Math.trunc(Number(value)).toLocaleString();
		return <span>{formattedValue}</span>;
	};

	const avgLikeCount = `${
		groupPerformances?.avgReelsLikeCount
			? Math.trunc(groupPerformances.avgReelsLikeCount).toLocaleString()
			: '-'
	}/${
		groupPerformances?.avgStyleClipLikeCount
			? Math.trunc(groupPerformances.avgStyleClipLikeCount).toLocaleString()
			: '-'
	}`;

	if (isLoading || !groupPerformances) {
		return (
			<Grid flex="between" wrap="wrap" _gap="16px">
				<DataContainer>
					{performanceKeys.map((key: GroupPerformanceKeys, index: number) => (
						<ReportSectionCard key={index} _width="350px" _height="100px">
							<Grid flex="start" align_items="start" direction="column">
								<ReportTopHeader>{performanceKeysMap[key]}</ReportTopHeader>
								<EmphasizeText>
									<CoreLoading color_type="gray" />
								</EmphasizeText>
							</Grid>
						</ReportSectionCard>
					))}
				</DataContainer>
			</Grid>
		);
	}

	return (
		<Grid flex="between" wrap="wrap" _gap="16px">
			{/* 3 columns으로 나오게 */}
			<DataContainer>
				{performanceKeys.map((key: GroupPerformanceKeys, index: number) => (
					<ReportSectionCard key={index} _width="350px" _height="100px">
						<Grid flex="start" align_items="start" direction="column">
							<ReportTopHeader>{performanceKeysMap[key]}</ReportTopHeader>
							<EmphasizeText>
								{key === 'avgLikeCount' && (
									<PerformanceValue performanceKey={key} value={avgLikeCount} />
								)}
								{key !== 'avgLikeCount' && (
									<PerformanceValue performanceKey={key} value={groupPerformances[key]} />
								)}
							</EmphasizeText>
						</Grid>
					</ReportSectionCard>
				))}
			</DataContainer>
		</Grid>
	);
}
export default CreatorAverageCard;

const { Grid } = style;

const DataContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	gap: 16px;
`;
